import {
    LoadModelInfo,
    ModelType,
    PredictiveModelInfo, ProsumerModelInfo,
    PvModelInfo,
    ThermalLoadModelInfo
} from 'smag-commons/cfg/prediction';
import {WindModelInfo} from 'smag-commons/cfg/prediction/models';

/**
 * A single configuration detail for a predictive model.
 */
export interface ModelDetail {
    param: string;
    value: number | boolean | string;
    uom?: string;
}

/**
 * Returns all {@link ModelDetail details} for the given {@link PredictiveModelInfo model}.
 *
 * @param model The predictive model
 * @return Model's details
 */
export function modelDetails(model: PredictiveModelInfo): ModelDetail[] {
    switch (model.type) {
        case ModelType.PV: {
            const details: ModelDetail[] = [
                {param: 'Power model', value: (model as PvModelInfo).power_model}
            ];
            if ((model as PvModelInfo).meteo_connector_id) {
                details.push({param: 'Meteo connector', value: (model as PvModelInfo).meteo_connector_id});
            }
            if ((model as PvModelInfo).meteo_virtual_device_id) {
                details.push({param: 'Meteo virtual device', value: (model as PvModelInfo).meteo_virtual_device_id});
            }
            if ((model as PvModelInfo).params) {
                details.push({
                        param: 'PV array area',
                        value: (model as PvModelInfo).params.s_pv,
                        uom: 'm^2'
                    },
                    {
                        param: 'Cell reference efficiency',
                        value: (model as PvModelInfo).params.eta_pv
                    },
                    {
                        param: 'Packing factor',
                        value: (model as PvModelInfo).params.p_f
                    }, {
                        param: 'System efficiency balance',
                        value: (model as PvModelInfo).params.eta_pc
                    });
            }
            return details;
        }
        case ModelType.WIND: {
            const windModel = (model as WindModelInfo);
            const details: ModelDetail[] = [
                {param: 'Power model', value: windModel.power_model}
            ];
            if (windModel.power_device) {
                details.push({param: 'Power device', value: windModel.power_device});
            }
            if (windModel.meteo_connector_id) {
                details.push({param: 'Meteo connector', value: windModel.meteo_connector_id});
            }
            if (windModel.meteo_virtual_device_id) {
                details.push({param: 'Meteo virtual device', value: windModel.meteo_virtual_device_id});
            }
            return details;
        }
        case ModelType.LOAD: {
            const details: ModelDetail[] = [
                {param: 'Active power model', value: (model as LoadModelInfo).active_power_model},
                {param: 'Active power measure key', value: (model as LoadModelInfo).active_power_device}
            ];
            if ((model as LoadModelInfo).reactive_power_model) {
                details.push({param: 'Reactive power model', value: (model as LoadModelInfo).reactive_power_model});
            }
            if ((model as LoadModelInfo).reactive_power_device) {
                details.push({
                    param: 'Reactive power measure key',
                    value: (model as LoadModelInfo).reactive_power_device
                });
            }
            return details;
        }
        case ModelType.THERMAL_LOAD:
            return [
                {param: 'Power model', value: (model as ThermalLoadModelInfo).power_model},
                {param: 'Power measure key', value: (model as ThermalLoadModelInfo).power_device}
            ] as ModelDetail[];
        case ModelType.PROSUMER:
            return [
                {param: 'Prosumer model', value: (model as ProsumerModelInfo).prosumer_model},
                {param: 'Absorbed power measure key', value: (model as ProsumerModelInfo).absorbed_power_device},
                {param: 'Injected power measure key', value: (model as ProsumerModelInfo).injected_power_device}
            ] as ModelDetail[];
        default:
            return [];
    }
}
